import React, { useEffect, useState } from 'react';
import type { FC } from 'react';

interface CredentialResponse {
    clientId: string;
    credential: string;
}

declare global {
    interface Window {
        google: {
            accounts: {
                id: {
                    renderButton(arg0: HTMLElement | null, arg1: Record<string, unknown>): unknown;
                    initialize: (config: {
                        client_id: string;
                        callback: (response: CredentialResponse) => void;
                    }) => void;
                    cancel: () => void;
                };
            };
        };
    }
}

interface User {
    _id: string;
}

interface GoogleSigninButtonProps {
    onSignIn?: (user: CredentialResponse) => void;
}

export const GoogleSigninButton: FC<GoogleSigninButtonProps> = ({ onSignIn }) => {
    const [gsiScriptLoaded, setGsiScriptLoaded] = useState(false);
    const [user, setUser] = useState<User | undefined>(undefined);

    useEffect(() => {
        if (user?._id || gsiScriptLoaded) return;

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.onload = initializeGsi;
        script.async = true;
        script.id = 'google-client-script';
        document.querySelector('body')?.appendChild(script);

        return () => {
            window.google?.accounts.id.cancel();
            document.getElementById('google-client-script')?.remove();
        };
    }, [gsiScriptLoaded, user?._id]);

    const initializeGsi = () => {
        if (!window.google || gsiScriptLoaded) return;

        setGsiScriptLoaded(true);
        window.google?.accounts.id.initialize({
            client_id: process.env.GOOGLE_OAUTH_CLIENT_ID || '',
            callback: handleGoogleSignIn,
        });

        window.google?.accounts.id.renderButton(document.querySelector('.g_id_signin'), {
            type: 'standard',
            use_fedcm_for_prompt: true,
            size: 'large',
            theme: 'filled_black',
            text: 'sign_in_with',
            shape: 'rectangular',
            logo_alignment: 'right',
            width: '400',
        });
    };

    const handleGoogleSignIn = async (res: CredentialResponse) => {
        if (!res.clientId || !res.credential) return;
        onSignIn?.(res);
    };

    return <button type="button" className="g_id_signin" />;
};
